import { useState } from "react";
import { ArrowRight, Heart, ShoppingBag, PiggyBank, Share2, TrendingUp, Copy, Check, UserPlus } from "lucide-react";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import ContactForm from "@/components/ContactForm";
import { HERBALIFE_ACCOUNT_URL } from "@/constants/products";

const COACH_IMG = "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHx3b21hbiUyMHlvZ2ElMjBsaWZlc3R5bGV8ZW58MHx8fHwxNzg4MDg3ODQwfDA&ixlib=rb-4.1.0&q=85";

const CopyBlock = ({ label, value, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error(e);
    }
  };
  return (
    <div className="rounded-3xl border border-forest/10 bg-white p-6 text-center shadow-sm">
      <p className="font-display text-xs font-bold tracking-[0.25em] text-forest/60">{label}</p>
      <p data-testid={`${testid}-value`} className="mt-3 select-all font-display text-3xl font-black tracking-wider text-coral md:text-5xl">
        {value}
      </p>
      <button
        onClick={copy}
        data-testid={`${testid}-copy-button`}
        className="mt-5 inline-flex items-center gap-2 rounded-full border border-forest/25 px-6 py-3 font-display text-xs font-bold tracking-[0.15em] text-forest transition-all duration-300 hover:scale-105 hover:border-coral hover:text-coral"
      >
        {copied ? <Check className="h-4 w-4 text-sage" /> : <Copy className="h-4 w-4" />}
        {copied ? "COPIED!" : "TAP TO COPY"}
      </button>
    </div>
  );
};

const GetStarted = () => {
  return (
    <main data-testid="get-started-page" className="pt-28">
      <section className="mx-auto max-w-7xl px-6 pb-16 pt-10 text-center">
        <h1 data-testid="get-started-headline" className="font-display text-4xl font-black uppercase tracking-tighter text-ink sm:text-5xl md:text-6xl">
          <MaskedLine delay={0.15}>Get started</MaskedLine>
          <MaskedLine delay={0.3}>
            <span className="text-coral">with Herbalife ❤️</span>
          </MaskedLine>
        </h1>
        <FadeUp delay={0.5}>
          <p className="mx-auto mt-6 max-w-xl font-body text-base font-light leading-relaxed text-ink/70 md:text-lg">
            Two simple ways to join — as a Preferred Member who shops for themselves, or as a Distributor who shares products with others.
          </p>
        </FadeUp>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 pt-20">
        <div className="mx-auto max-w-2xl text-center">
          <FadeUp>
            <p className="font-display text-xs font-bold tracking-[0.3em] text-coral">OPTION 1 — PREFERRED MEMBER</p>
            <h2 data-testid="member-headline" className="mt-4 font-display text-3xl font-black uppercase tracking-tight text-ink md:text-4xl">
              Become a Preferred Member & save ❤️
            </h2>
            <p className="mt-5 font-body text-base font-light leading-relaxed text-ink/70">
              Want to save on the Herbalife products you already love? A Preferred Member account may give you access to member pricing and other benefits.
            </p>
            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-4 rounded-3xl border border-forest/10 bg-white p-6 shadow-sm">
                <ShoppingBag className="mt-1 h-7 w-7 shrink-0 text-coral" strokeWidth={1.8} />
                <div>
                  <h3 className="font-display text-base font-extrabold tracking-tight text-ink">SHOP FOR YOURSELF</h3>
                  <p className="mt-1 font-body text-sm font-light leading-relaxed text-ink/60">
                    Purchase Herbalife products for your own personal use with member benefits according to current Herbalife terms.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 rounded-3xl border border-forest/10 bg-white p-6 shadow-sm">
                <PiggyBank className="mt-1 h-7 w-7 shrink-0 text-coral" strokeWidth={1.8} />
                <div>
                  <h3 className="font-display text-base font-extrabold tracking-tight text-ink">MEMBER SAVINGS</h3>
                  <p data-testid="member-savings" className="mt-1 font-display text-3xl font-black tracking-tight text-forest">
                    20%–45%
                  </p>
                  <p className="mt-1 font-body text-sm font-light text-ink/60">Potential member savings.</p>
                  <p className="mt-3 font-body text-xs font-light leading-relaxed text-ink/45">
                    Discount levels, eligibility, pricing and membership terms are subject to Herbalife's current policies and may change.
                  </p>
                </div>
              </div>
            </div>
            <a
              href={HERBALIFE_ACCOUNT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="become-member-button"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-coral px-9 py-4 font-display text-sm font-bold tracking-[0.15em] text-bone shadow-lg shadow-coral/30 transition-all duration-300 hover:scale-105 hover:bg-coraldark"
            >
              BECOME A PREFERRED MEMBER
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <p data-testid="member-sponsor-note" className="mt-6 max-w-lg rounded-2xl border border-coral/30 bg-coral/5 p-5 font-body text-sm font-light leading-relaxed text-ink/70">
              When asked for sponsor information, enter the Sponsor Herbalife ID <span className="font-bold text-coral">10XY092788</span> and the 3 first letters <span className="font-bold text-coral">GON</span>.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="bg-sage/15 px-6 py-24">
        <div className="mx-auto max-w-7xl">
          <FadeUp className="text-center">
            <p className="font-display text-xs font-bold tracking-[0.3em] text-coral">OPTION 2 — BECOME A DISTRIBUTOR</p>
            <h2 data-testid="distributor-headline" className="mt-4 font-display text-3xl font-black uppercase tracking-tight text-ink md:text-4xl">
              Want to sell Herbalife?
            </h2>
            <p className="mx-auto mt-5 max-w-xl font-body text-base font-light leading-relaxed text-ink/70">
              Become a Herbalife Distributor and have the opportunity to purchase products for yourself and share products with customers.
            </p>
          </FadeUp>
          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {[
              { icon: ShoppingBag, title: "BUY", text: "Purchase products according to current distributor pricing and policies.", testid: "distributor-benefit-buy" },
              { icon: Share2, title: "SHARE", text: "Share products you enjoy with friends, family and customers.", testid: "distributor-benefit-share" },
              { icon: TrendingUp, title: "BUILD", text: "Build a Herbalife business according to Herbalife's current terms and policies.", testid: "distributor-benefit-build" },
            ].map((b, i) => (
              <FadeUp key={b.title} delay={0.08 * i}>
                <div data-testid={b.testid} className="h-full rounded-3xl border border-forest/10 bg-white p-8 text-center shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-coral/15">
                    <b.icon className="h-6 w-6 text-coral" strokeWidth={1.8} />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-extrabold tracking-[0.1em] text-ink">{b.title}</h3>
                  <p className="mt-2 font-body text-sm font-light leading-relaxed text-ink/60">{b.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp className="mt-10 text-center">
            <p className="mx-auto max-w-2xl font-body text-xs font-light leading-relaxed text-ink/45">
              Distributor pricing, discounts, fees, qualifications, income potential and business opportunities are subject to Herbalife's current terms and policies.
            </p>
            <a
              href={HERBALIFE_ACCOUNT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="become-distributor-button"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-forest px-9 py-4 font-display text-sm font-bold tracking-[0.15em] text-bone transition-all duration-300 hover:scale-105 hover:bg-coral"
            >
              BECOME A DISTRIBUTOR
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-24">
        <FadeUp className="text-center">
          <UserPlus className="mx-auto h-10 w-10 text-coral" strokeWidth={1.5} />
          <h2 data-testid="registration-headline" className="mt-6 font-display text-3xl font-black uppercase tracking-tight text-ink md:text-5xl">
            Let's get you started!
          </h2>
          <p className="mx-auto mt-4 max-w-md font-body text-base font-light leading-relaxed text-ink/70">
            Ready to create your Herbalife account? Follow these simple steps.
          </p>
        </FadeUp>

        <FadeUp className="mt-12">
          <div data-testid="sponsor-callout" className="rounded-3xl border-2 border-coral/40 bg-coral/5 p-8">
            <p className="text-center font-display text-sm font-black tracking-[0.2em] text-coral">
              ⚠️ IMPORTANT — DO THIS RIGHT AWAY
            </p>
            <p className="mt-3 text-center font-body text-base font-light leading-relaxed text-ink/70">
              When asked for sponsor information during registration, enter these two things:
            </p>
            <div className="mt-8 grid gap-6 sm:grid-cols-2">
              <CopyBlock label="YOUR SPONSOR HERBALIFE ID" value="10XY092788" testid="sponsor-id" />
              <CopyBlock label="3 FIRST LETTERS" value="GON" testid="sponsor-letters" />
            </div>
          </div>
        </FadeUp>

        <div className="mt-14 space-y-10">
          <FadeUp>
            <div className="flex flex-col items-center gap-5 rounded-3xl border border-forest/10 bg-white p-8 text-center shadow-sm">
              <span className="font-serif text-5xl font-medium italic leading-none text-sage/60">01</span>
              <p className="font-body text-base font-light text-ink/70">Click below to begin:</p>
              <a
                href={HERBALIFE_ACCOUNT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="create-account-button"
                className="inline-flex items-center gap-2 rounded-full bg-coral px-9 py-4 font-display text-sm font-bold tracking-[0.15em] text-bone shadow-lg shadow-coral/30 transition-all duration-300 hover:scale-105 hover:bg-coraldark"
              >
                CREATE YOUR ACCOUNT
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </FadeUp>

          <FadeUp>
            <div className="flex flex-col items-center gap-3 rounded-3xl border border-forest/10 bg-white p-8 text-center shadow-sm">
              <span className="font-serif text-5xl font-medium italic leading-none text-sage/60">02</span>
              <p className="font-body text-base font-light text-ink/70">Complete the registration information.</p>
            </div>
          </FadeUp>

          <FadeUp>
            <div className="flex flex-col items-center gap-3 rounded-3xl border border-forest/10 bg-white p-8 text-center shadow-sm">
              <span className="font-serif text-5xl font-medium italic leading-none text-sage/60">03</span>
              <p className="font-body text-base font-light text-ink/70">
                When asked for sponsor information, enter the Sponsor Herbalife ID <span className="font-semibold text-coral">10XY092788</span> and the 3 first letters <span className="font-semibold text-coral">GON</span> shown above.
              </p>
            </div>
          </FadeUp>

          <FadeUp>
            <div className="flex flex-col items-center gap-3 rounded-3xl border border-forest/10 bg-white p-8 text-center shadow-sm">
              <span className="font-serif text-5xl font-medium italic leading-none text-sage/60">04</span>
              <p className="font-body text-base font-light text-ink/70">Finish registration and follow the current instructions provided by Herbalife.</p>
            </div>
          </FadeUp>
        </div>

        <FadeUp className="mt-12 text-center">
          <p data-testid="registration-disclaimer" className="mx-auto max-w-2xl font-body text-xs font-light leading-relaxed text-ink/45">
            Membership fees, discounts, benefits, registration requirements and other terms are subject to Herbalife's current policies and may change. Please review the current information provided by Herbalife before completing registration.
          </p>
        </FadeUp>
      </section>

      <section id="contact" data-testid="contact-section" className="grain relative overflow-hidden bg-sage/20 px-6 py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 md:grid-cols-2">
          <FadeUp>
            <span className="inline-flex items-center gap-2 rounded-full border border-forest/20 bg-bone/80 px-4 py-1.5 font-display text-[11px] font-semibold tracking-[0.25em] text-forest">
              <Heart className="h-3.5 w-3.5 fill-coral text-coral" /> CONTACT ME
            </span>
            <h2 data-testid="contact-headline" className="mt-6 font-display text-4xl font-black uppercase tracking-tight text-ink md:text-5xl">
              Need a wellness coach? 💕
            </h2>
            <p data-testid="contact-text" className="mt-6 max-w-md font-body text-base font-light leading-relaxed text-ink/75">
              Have questions about getting started, choosing products, or creating a wellness routine? I'd love to hear from you! Send me a message below and let's connect.
            </p>
            <ContactForm />
            <p data-testid="contact-disclaimer" className="mt-8 max-w-md font-body text-xs font-light leading-relaxed text-ink/50">
              Please note: I provide general wellness and product guidance, not medical advice. For medical questions or concerns, please consult your qualified healthcare professional.
            </p>
          </FadeUp>
          <FadeUp delay={0.2} className="relative">
            <span className="pointer-events-none absolute -top-10 right-2 z-10 font-serif text-8xl font-medium italic text-coral/50">
              say hi
            </span>
            <div className="overflow-hidden rounded-t-[12rem] rounded-b-3xl border border-forest/10 shadow-2xl shadow-forest/10">
              <img
                src={COACH_IMG}
                alt="Peaceful yoga silhouette at golden hour"
                className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
};

export default GetStarted;
